import usaStates from '../../../../../../../../util/usa-states';

/**
 * Attemps to convert a full US state name to its abbreviation.
 *
 * @param {string} [state] The US state.
 * @returns {string}
 */
export const convertStateToAbbreviation = state => {
  if (!state) {
    return '';
  }

  const trimmed = state.trim().replace(/\s\s+/g, ' ');
  const match = usaStates.find(
    usaState => usaState.name.toLowerCase() === trimmed.toLowerCase(),
  );

  return match?.abbreviation || trimmed;
};

/**
 * The expected column properties a CSV/Spreadsheet should contain.
 * Typically those will be the header of the spreadsheet.
 *
 * The `matcher` property is a regex that will be used on the header string of each
 * column to automatically try and match the column to our expected property.
 *
 * This way the order of the columns does not matter.
 *
 * For example, the header looks like this:
 *
 * ```.
 * "Last" | "First" | "Phone"
 * ===========================
 * ```.
 *
 * The regexes here get matched to those strings so the 1st column
 * will be matched to `lastName`, the 2nd column to `firstName` and the
 * 3rd column ot `phoneNumber`. The user of course has the ability to manually
 * change the matchings via a dropdown.
 */
export const columnProperties = [
  {
    key: 'firstName',
    label: 'First name',
    matcher: /first name|first|firstname/i,
  },
  {
    key: 'lastName',
    label: 'Last name',
    matcher: /last name|last|lastname|surname/i,
  },
  {
    key: 'dateOfBirth',
    label: 'Date of birth',
    matcher: /date of birth|birth|birthday/i,
  },
  {
    key: 'phoneNumber',
    label: 'Mobile number(s)',
    matcher: /phone|mobile|phone number/i,
  },
  {
    key: 'emailAddress',
    label: 'Email address(es)',
    matcher: /email|emails|email address|email addresses/i,
  },
  // Setting address Line 2 first ensures it can be matched and not be
  // confused with Address Line 1, which matches any line with `address`
  {
    key: 'addressLine2',
    label: 'Address Line 2',
    matcher: /address line 2|other address/i,
  },
  {
    key: 'addressLine1',
    label: 'Address Line 1',
    matcher: /address|street address/i,
  },
  {
    key: 'city',
    label: 'City',
    matcher: /city|town/i,
  },
  {
    key: 'state',
    label: 'State',
    matcher: /state/i,
  },
  {
    key: 'zipCode',
    label: 'Zip Code',
    matcher: /zip|code|postal|post code/i,
  },
  {
    key: 'organizationRefId',
    label: 'Ref ID',
    matcher: /identifier|reference id|ref|ref id|student id|student|id/i,
  },
  {
    key: 'selfieFilename',
    label: 'Image Filename',
    matcher:
      /filename|image filename|selfie filename|file name|image file name|selfie file name/i,
  },
  {
    key: 'selfiePhotoUrl',
    label: 'URL Link',
    matcher: /selfie|avatar|photo|picture|url link|cloud url/i,
  },
  {
    key: 'gender',
    label: 'Gender',
    matcher: /gender/i,
  },
  {
    key: 'sport',
    label: 'Sport',
    matcher: /sport/i,
  },
  {
    key: 'level',
    label: 'Level',
    matcher: /level/i,
  },
  {
    key: 'team',
    label: 'Team',
    matcher: /team/i,
  },
  {
    key: 'jerseyNumber',
    label: 'Jersey Number',
    matcher: /jersey|jersey no|jersey number/i,
  },
  {
    key: 'grade',
    label: 'Grade',
    matcher: /grade/i,
  },
  {
    key: 'className',
    label: 'Home Room #',
    matcher: /class|classroom|home room|homeroom/i,
  },
  {
    key: 'teacherName',
    label: 'Teacher',
    matcher: /teacher|teacher name|homeroom teacher/i,
  },
  {
    key: 'department',
    label: 'Department',
    matcher: /department/i,
  },
  {
    key: 'period',
    label: 'Period',
    matcher: /period/i,
  },
  {
    key: 'track',
    label: 'Track',
    matcher: /track/i,
  },
  {
    key: 'title',
    label: 'Title',
    matcher: /title/i,
  },
];

/**
 * Runs the heading regex and returns the first matched column property.
 *
 * @param {columnProperties} properties The column properties.
 * @param {String} heading The heading string as matched by a matcher regex.
 * @returns {string}
 */
export const getHeadingMatchedPropertyKey = (properties, heading) => {
  return (
    properties.find(property => RegExp(property.matcher).test(heading))?.key ??
    ''
  );
};

/**
 * Maps the matched columns to actual data that can be used to rosterIdentityCreate.
 *
 * @param {Array<Object>} columns Column state as returned from the ColumnMatcher view.
 * @param {Array<any>} rows The parsed data rows of the spreadsheet.
 * @returns {Array<Object>}
 */
export const mapMatchedColumnsToRowData = (columns, rows) => {
  return rows.map(row => {
    return columns
      .filter(column => column.matchedPropertyKey !== 'skip')
      .reduce((acc, column) => {
        return {
          ...acc,
          [column.matchedPropertyKey]: row[column.rowDataIndex],
        };
      }, {});
  });
};

/**
 * Attempts to determine whether the provided spreadsheet data contain a header row.
 * It takes the first row and compares every data point wth the matches in `columnProperties` above.
 * If more than half match correctly we can be pretty sure the file contains a header.
 *
 * @param {columnProperties} properties The column properties.
 * @param {Array<[]any>} data The as returned from the parser.
 * @returns {boolean}
 */
export const importDataContainsHeader = (properties, data) => {
  const [head] = data ?? [];

  if (!head || !head.length) {
    return false;
  }

  return (
    head.filter(label => getHeadingMatchedPropertyKey(properties, label))
      .length >
    head.length / 2
  );
};

/**
 * Returns a list of column properties based off of an array of keys
 *
 * @param {Array<String>} keys List of keys.
 * @returns {Array<Object>}
 */
export const getColumnPropertiesByKey = (keys = []) => {
  return keys
    .map(key => columnProperties.find(property => property.key === key))
    .filter(Boolean);
};
